import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { Grid, CircularProgress, FormHelperText, Typography } from '@mui/material';
import { StepperControls, StepWrapper, StyledCardSection, Button } from '@fnol/library/src/components';
import { common } from '@fnol/library/src/lib';
import { legalPersonType, personType, requestType, translations } from '../../lib/consts';
import { useValidateIndividual } from '../../lib/customHooks';
import InjuredPersonsTable from '../InjuredPersonsTable';
import Individual from '../Individual';
import { IndividualCleanup } from '../Individual/Individual';

type GenericObject = { [key: string]: any };

type Props = {
    navigator: GenericObject[];
};

const InjuredScreen = ({ navigator }: Props) => {
    const { values, setErrors, setTouched, setFieldValue, submitForm, isSubmitting } = useFormikContext<any>();
    const [currentStep, { goForward, goBack }] = navigator;
    const [inputPersonOpen, setInputPersonOpen] = useState(false);
    const [inputPersonId, setInputPersonId] = useState(0);
    const [addPossible, setAddPossible] = useState(true);
    const individualValidation = useValidateIndividual(personType.injured);

    values.mobileLabelStar = false;

    const handleNext = (): void => {
        const filteredClaims = values.reportedClaims?.filter((claim: GenericObject) => !!claim.jmbg);
        if (
            values.submitterIndividualReportingImmaterialDamage === 'true' ||
            values.ownerIndividualReportingImmaterialDamage === 'true' ||
            values.ownerBuildingIndividualReportingImmaterialDamage === 'true' ||
            values.driverIndividualReportingImmaterialDamage === 'true' ||
            values.userIndividualReportingImmaterialDamage === 'true' ||
            (values.injured1IndividualName && values.injured1IndividualName !== '') ||
            (values.injured2IndividualName && values.injured2IndividualName !== '') ||
            (values.injured3IndividualName && values.injured3IndividualName !== '') ||
            (values.injured4IndividualName && values.injured4IndividualName !== '') ||
            (values.injured5IndividualName && values.injured5IndividualName !== '') ||
            filteredClaims.length !== 0
        ) {
            submitForm();
        } else {
            setFieldValue('submitError', translations.INJURED_PERSONS_REQUIRED);
        }
    };

    const checkNextInjuredPersonId = () => {
        const availableIds = [1, 2, 3, 4, 5];
        let nextAvailableId = 0;
        availableIds.map((availableId) => {
            if (!values[`injured${availableId}IndividualName`] && nextAvailableId === 0) {
                nextAvailableId = availableId;
            }
        });
        return nextAvailableId;
    };

    const checkIfUCNHasImmaterial = (ucn: string) => {
        const checkIds = [1, 2, 3, 4, 5];
        let exists = false;
        if (
            values.submitterType === legalPersonType.individual &&
            values.submitterIndividualReportingImmaterialDamage === 'true' &&
            values.submitterIndividualUCN === ucn
        ) {
            exists = true;
        }
        if (
            values.ownerType &&
            values.ownerType === legalPersonType.individual &&
            values.ownerIndividualReportingImmaterialDamage === 'true' &&
            values.ownerBuildingIndividualUCN === ucn
        ) {
            exists = true;
        }
        if (
            values.driverType &&
            values.driverIndividualReportingImmaterialDamage === 'true' &&
            values.driverIndividualUCN === ucn
        ) {
            exists = true;
        }
        if (
            values.userType &&
            values.userType === legalPersonType.individual &&
            values.userIndividualReportingImmaterialDamage === 'true' &&
            values.userIndividualUCN === ucn
        ) {
            exists = true;
        }
        checkIds.map((checkId) => {
            if (
                values[`injured${checkId}IndividualUCN`] &&
                values[`injured${checkId}IndividualUCN`] === ucn &&
                checkId !== inputPersonId
            ) {
                exists = true;
            }
        });
        return exists;
    };

    const saveInjuredPersonToId = (idToSaveTo: number) => {
        if (individualValidation.isValid) {
            const filteredClaims = values.reportedClaims.filter(
                (claim: GenericObject) => claim.jmbg === values.injuredIndividualUCN,
            );
            setTouched(
                {
                    injuredIndividualUCN: true,
                },
                false,
            );
            const existingErrors: GenericObject = {};
            if (checkIfUCNHasImmaterial(values.injuredIndividualUCN)) {
                existingErrors.injuredIndividualUCN = translations.validationMessages.ALREADY_HAS_IMMATERIAL;
                setErrors(existingErrors);
            } else if (filteredClaims.length !== 0) {
                existingErrors.injuredIndividualUCN = translations.validationMessages.CLAIM_ALREADY_EXISTS_FOR_UCN;
                setErrors(existingErrors);
            } else {
                setFieldValue(`injured${idToSaveTo}IndividualName`, values.injuredIndividualName);
                setFieldValue(`injured${idToSaveTo}IndividualLastName`, values.injuredIndividualLastName);
                setFieldValue(`injured${idToSaveTo}IndividualUCN`, values.injuredIndividualUCN);
                setFieldValue(`injured${idToSaveTo}IndividualCountry`, values.injuredIndividualCountry);
                setFieldValue(`injured${idToSaveTo}IndividualMyCountry`, values.injuredIndividualMyCountry);
                setFieldValue(`injured${idToSaveTo}IndividualCity`, values.injuredIndividualCity);
                setFieldValue(`injured${idToSaveTo}IndividualCityInSerbia`, values.injuredIndividualCityInSerbia);
                setFieldValue(`injured${idToSaveTo}IndividualStreet`, values.injuredIndividualStreet);
                setFieldValue(`injured${idToSaveTo}IndividualPhone`, values.injuredIndividualPhone);
                setFieldValue(`injured${idToSaveTo}IndividualEmail`, values.injuredIndividualEmail);
                setFieldValue(
                    `injured${idToSaveTo}IndividualSubmitterRelationId`,
                    values.injuredIndividualSubmitterRelationId,
                );
                setFieldValue(
                    `injured${idToSaveTo}IndividualReportingImmaterialDamage`,
                    values.injuredIndividualReportingImmaterialDamage,
                );
                setFieldValue(`injured${idToSaveTo}IndividualPersonRoleId`, values.injuredIndividualPersonRoleId);
                setFieldValue(
                    `injured${idToSaveTo}IndividualImmaterialDamageDescription`,
                    values.injuredIndividualImmaterialDamageDescription,
                );
                if (idToSaveTo === 5) {
                    setAddPossible(false);
                }
                IndividualCleanup(personType.injured, setFieldValue);
                setInputPersonId(0);
                setInputPersonOpen(false);
            }
        }
    };

    const handleCancel = () => {
        IndividualCleanup(personType.injured, setFieldValue);
        setInputPersonId(0);
        setInputPersonOpen(false);
    };

    const handleSave = (idToEdit: number) => {
        setTouched(
            {
                ...individualValidation.touched,
            },
            false,
        );
        setErrors(individualValidation.errors);
        if (individualValidation.isValid) {
            const availableId = checkNextInjuredPersonId();
            if (idToEdit === 0 && availableId !== 0) {
                saveInjuredPersonToId(availableId);
            } else {
                if (idToEdit !== 0) {
                    saveInjuredPersonToId(idToEdit);
                } else {
                    return false;
                }
            }
        }
    };

    const handleGoBack = (): void => {
        setFieldValue('submitError', '');
        goBack();
    };

    useEffect(() => {
        const nextAvailableId = checkNextInjuredPersonId();
        if (nextAvailableId === 0) {
            setAddPossible(false);
        }
    }, [addPossible]);

    useEffect(() => {
        if (values.submittedSuccessfully) {
            goForward();
        }
    }, [values.submittedSuccessfully]);

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="subtitle1">
                    {translations.INJURED_PERSONS_TITLE}
                </Typography>
                <InjuredPersonsTable
                    inputPersonOpen={inputPersonOpen}
                    setInputPersonOpen={setInputPersonOpen}
                    setInputPersonId={setInputPersonId}
                    setAddPossible={setAddPossible}
                />
                <Grid container justifyContent="center">
                    <Button
                        onClickHandler={() => setInputPersonOpen(true)}
                        label={translations.INJURED_PERSONS_ADD_NEW_PERSON}
                        disabled={inputPersonOpen || !addPossible}
                        fullWidth={false}
                    />
                </Grid>
            </StyledCardSection>
            {!values.submittedSuccessfully && values.submitError !== '' ? (
                <FormHelperText error={true}>
                    <>{values.submitError}</>
                </FormHelperText>
            ) : (
                <></>
            )}
            {inputPersonOpen ? (
                <StyledCardSection>
                    <Individual individualType={personType.injured} id={inputPersonId} />
                    <Grid container>
                        <Grid container item={true} xs={6}>
                            <Button
                                onClickHandler={handleCancel}
                                label={translations.INJURED_PERSONS_CANCEL}
                                fullWidth={false}
                            />
                        </Grid>
                        <Grid container item={true} xs={6} justifyContent="flex-end">
                            <Button
                                onClickHandler={() => handleSave(inputPersonId)}
                                label={translations.INJURED_PERSONS_SAVE}
                                fullWidth={false}
                                disabled={values.personIsDriver}
                            />
                        </Grid>
                    </Grid>
                </StyledCardSection>
            ) : (
                <>
                    {isSubmitting ? (
                        <CircularProgress />
                    ) : (
                        <StepperControls onPrevious={handleGoBack} onNext={handleNext} onNextLabel={common.NEXT} />
                    )}
                </>
            )}
        </StepWrapper>
    );
};

export default InjuredScreen;
